import axios from "axios";
import { httpClient } from "../http/client";
import { getSubscriptionState, setSubscriptionState } from "./subscriptionStore";
import type {
  CheckoutOrderResponse,
  CheckoutVerifyPayload,
  CheckoutVerifyResponse,
  StartPremiumCheckoutOptions,
  SubscriptionPlan,
  SubscriptionSnapshot,
} from "./types";

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

type RazorpayCheckoutResponse = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

type RazorpayOptions = {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description?: string;
  handler: (response: RazorpayCheckoutResponse) => void | Promise<void>;
  modal?: {
    ondismiss?: () => void;
  };
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme?: {
    color?: string;
  };
};

interface RazorpayCheckout {
  open(): void;
  on(event: "payment.failed", handler: (response: { error?: { description?: string } }) => void): void;
}

type RazorpayConstructor = new (options: RazorpayOptions) => RazorpayCheckout;

let razorpayScriptPromise: Promise<void> | null = null;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (isObject(data)) {
      const message = data.message;
      const detail = data.detail;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
      if (typeof detail === "string" && detail.trim()) {
        return detail;
      }
    }
    return error.message || "Request failed. Please try again.";
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}

function readPayloadObject(value: unknown): Record<string, unknown> | null {
  if (!isObject(value)) {
    return null;
  }
  if ("data" in value && isObject((value as { data: unknown }).data)) {
    return (value as { data: Record<string, unknown> }).data;
  }
  return value;
}

function readExpiresAt(obj: Record<string, unknown>): string | null {
  const direct = obj.expiresAt;
  if (typeof direct === "string" && direct.trim()) {
    return direct;
  }
  const snake = obj.expires_at;
  if (typeof snake === "string" && snake.trim()) {
    return snake;
  }
  return null;
}

function normalizePlan(value: unknown): SubscriptionPlan {
  if (typeof value !== "string") {
    return "FREE";
  }
  const p = value.trim().toUpperCase();
  if (p === "PREMIUM" || p === "PRO" || p === "PAID") {
    return "PREMIUM";
  }
  return "FREE";
}

function parseSubscriptionSnapshot(value: unknown): SubscriptionSnapshot {
  const raw = readPayloadObject(value) ?? (isObject(value) ? value : null);
  if (!raw) {
    return { plan: "FREE", premiumActive: false, expiresAt: null };
  }

  const planFromApi = normalizePlan(raw.plan);
  const premiumActiveFlag =
    raw.premiumActive === true ||
    raw.premium_active === true ||
    (typeof raw.premiumActive === "string" && raw.premiumActive.toLowerCase() === "true") ||
    (typeof raw.premium_active === "string" && raw.premium_active.toLowerCase() === "true");
  const premiumActive = planFromApi === "PREMIUM" || premiumActiveFlag;
  const plan: SubscriptionPlan = premiumActive ? "PREMIUM" : "FREE";
  const expiresAt = readExpiresAt(raw);

  return { plan, premiumActive, expiresAt };
}

export function isPremiumActive(snapshot?: SubscriptionSnapshot | null): boolean {
  return Boolean(snapshot?.premiumActive && snapshot?.plan === "PREMIUM");
}

export async function fetchMySubscription(): Promise<SubscriptionSnapshot> {
  const response = await httpClient.get<unknown>("/api/v1/subscription/me");
  return parseSubscriptionSnapshot(response.data);
}

export async function refreshSubscriptionState(): Promise<SubscriptionSnapshot> {
  const current = getSubscriptionState();
  setSubscriptionState({
    status: "loading",
    snapshot: current.snapshot,
    errorMessage: null,
  });

  try {
    const snapshot = await fetchMySubscription();
    setSubscriptionState({
      status: "ready",
      snapshot,
      errorMessage: null,
    });
    return snapshot;
  } catch (error) {
    setSubscriptionState({
      status: "error",
      snapshot: current.snapshot,
      errorMessage: parseErrorMessage(error),
    });
    throw error;
  }
}

export async function createCheckoutOrder(): Promise<CheckoutOrderResponse> {
  const response = await httpClient.post<CheckoutOrderResponse>("/api/v1/subscription/checkout/order", {});
  return response.data;
}

export async function verifyCheckoutPayment(payload: CheckoutVerifyPayload): Promise<CheckoutVerifyResponse> {
  const response = await httpClient.post<CheckoutVerifyResponse>("/api/v1/subscription/checkout/verify", payload, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
}

async function ensureRazorpayScriptLoaded(): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("Razorpay is only available in browser context.");
  }

  if (window.Razorpay) {
    return;
  }

  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Failed to load Razorpay checkout script.")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay checkout script."));
      document.body.appendChild(script);
    }).finally(() => {
      if (!window.Razorpay) {
        razorpayScriptPromise = null;
      }
    });
  }

  await razorpayScriptPromise;
}

function getRazorpayConstructor(): RazorpayConstructor {
  if (!window.Razorpay) {
    throw new Error("Razorpay checkout is not available.");
  }
  return window.Razorpay;
}

export async function startPremiumCheckout(options: StartPremiumCheckoutOptions = {}): Promise<SubscriptionSnapshot> {
  options.onStatusChange?.("creating_order");
  const order = await createCheckoutOrder();

  await ensureRazorpayScriptLoaded();
  options.onStatusChange?.("opening_checkout");

  return new Promise<SubscriptionSnapshot>((resolve, reject) => {
    const Razorpay = getRazorpayConstructor();

    const checkout = new Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.orderId,
      name: "HallTicket Pro",
      description: "One-time Premium Plan",
      prefill: options.prefill,
      handler: async (response) => {
        try {
          options.onStatusChange?.("verifying_payment");
          await verifyCheckoutPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });

          options.onStatusChange?.("refreshing_subscription");
          const latestSubscription = await refreshSubscriptionState();
          resolve(latestSubscription);
        } catch (error) {
          reject(new Error(parseErrorMessage(error)));
        }
      },
      modal: {
        ondismiss: () => {
          reject(new Error("Checkout dismissed by user."));
        },
      },
      theme: {
        color: "#4f6ef7",
      },
    });

    checkout.on("payment.failed", (response) => {
      const message = response.error?.description?.trim() || "Payment failed. Please try again.";
      reject(new Error(message));
    });

    checkout.open();
  });
}
